import { useEffect, useState } from 'react';
import SwiperType from 'swiper';
import { useSwiper } from 'swiper/react';

interface SwiperReactiveHook extends SwiperType {
  imagesLoaded: number;
}

/**
 * Custom hook to extend Swiper's functionalities and use it reactively.
 *
 * The standard Swiper API does not provide real-time updates for certain properties like `activeIndex`.
 * This hook is a workaround to address these limitations and use the swiper instance in a more reactive way.
 *
 * @returns {Object} - Contains:
 * - `swiper`: The Swiper instance.
 * - `imagesLength`: The number of images loaded in Swiper.
 */
export const useSwiperReactive = () => {
  const swiper = useSwiper();
  const [imagesLength, setImagesLength] = useState(0);

  // State to force a rerender, which allows for a real-time update of the activeIndex.
  // This is not built into the Swiper API by default.
  const [, setSignal] = useState({});
  const forceRerender = () => setSignal({});

  useEffect(() => {
    if (swiper) {
      // Attach the slideChange event to force a rerender whenever the slide changes.
      swiper.on('slideChange', forceRerender);
    }
    // Swiper type definitions do not include the `imagesLoaded` property.
    // However, it's available in the actual instance and seems to be loaded asynchronously.
    // We need to check if it exists to prevent runtime errors.
    if (imagesLength === 0 && swiper && 'imagesLoaded' in swiper) {
      const reactiveSwiper = swiper as SwiperReactiveHook;
      setImagesLength(reactiveSwiper.imagesLoaded);
    }
  });

  return { swiper, imagesLength };
};
